/**
 * moduleActions — SINGLE SOURCE OF TRUTH for the per-module `vibe:module-action`
 * verbs an agent (Pi chat / voice) can dispatch to an on-screen module.
 *
 * WHY: pre-refactor the verb catalog was hard-coded in TWO prompts — the Pi
 * `ui_action` tool description (piTools/uiActionTool.ts) and the voice
 * dispatcher system prompt (backend voice_dispatcher.py). Both drifted: new
 * modules (Mailcow, Mattermost, Nextcloud, ComfyUI) were never added to one or
 * both. Now the verbs live HERE, are attached to each on-screen module's SEE
 * snapshot in getSpaContext(), and BOTH surfaces render them from that payload.
 * Adding a module's verbs = edit THIS file only. The drift guard in
 * __tests__/moduleActions.test.ts reads each module's actual App.tsx handler
 * and fails if this catalog diverges.
 *
 * Scope: MODULE-scoped verbs only (they grow as modules are added — the drift
 * source). Host tiling verbs (open/close/focus/dock/maximize) are always
 * available and live in TILING_ACTIONS. Core-VIEW verbs (Knowledge set_nav,
 * Terminals set_terminals_view, Deploys refresh) are NOT modules and stay
 * static in the prompts — they do not drift when a module is added.
 * EXCEPTION (2026-07-13): `view:designer` lives HERE because getSpaContext's
 * withActions() attaches catalog verbs by contextKey for core views too — so
 * the Designer preview's blockworld_cmd rides the same single source of truth.
 *
 * Keys are the spaContextStore `contextKey` (moduleId for modules, `view:{v}`
 * for core views).
 */

export interface ModuleActionSpec {
  /** The `action` string the module's handler compares against. */
  verb: string;
  /** Human-readable param hint, e.g. "{tab: mailboxes|aliases}". Omit for {}. */
  params?: string;
  /** One-line description of what the verb does. */
  description: string;
}

/** moduleId → the verbs its App.tsx `vibe:module-action` handler accepts.
 * MUST stay in lockstep with each handler (enforced by the drift-guard test). */
export const MODULE_ACTIONS: Record<string, ModuleActionSpec[]> = {
  'gamemagick.ghl': [
    { verb: 'set_tab', params: '{tab: campaign|pages|contacts|pipeline|conversations|calendar}', description: 'switch the CRM tab (campaign = the scoped-tenant dashboard, pages = campaign marketing pages + signups; both hidden for the Location owner)' },
    { verb: 'set_theme', params: '{theme: vivid|aurora|slate|minimal}', description: 'change the module theme' },
    { verb: 'refresh', description: 'reload the current tab' },
    { verb: 'open_external', description: 'open the CRM in a new browser tab' },
  ],
  'gamemagick.n8n-workflows': [
    { verb: 'select_workflow', params: '{id}', description: 'open a workflow from the on-screen list' },
    { verb: 'refresh', description: 'reload the workflow list' },
    { verb: 'open_external', description: 'open the n8n editor in a new tab' },
  ],
  'gamemagick.comfyui': [
    { verb: 'set_tab', params: '{tab: generate|video|inventory|library|status}', description: 'switch the ComfyUI tab' },
    { verb: 'select_workflow', params: '{id}', description: 'select an image workflow' },
    { verb: 'generate', params: '{prompt, workflow_id?}', description: 'queue an image generation (announce it is generating)' },
    { verb: 'generate_video', params: '{start_number|start_image_id, prompt, length?}', description: 'animate a library image into a WAN 2.2 clip (image-to-video; pass the image’s catalog number as start_number, e.g. "make image 7 a video"; announce it is rendering)' },
    { verb: 'refresh', description: 'reload the library/status' },
    { verb: 'open_image', params: '{number|id}', description: 'open a library image by its catalog number (the #N badge on each tile, e.g. "show image 7") or raw id' },
    { verb: 'open_video', params: '{number|id}', description: 'open a persisted library video by its catalog number (#N) or raw id' },
  ],
  'gamemagick.mattermost': [
    { verb: 'select_channel', params: '{name}', description: 'open a channel by name' },
    { verb: 'refresh', description: 'reload the active channel' },
    { verb: 'post', params: '{text}', description: 'PREFILL the composer only — the human must click Confirm send; never posts directly' },
  ],
  'gamemagick.mailcow': [
    // B3 (2026-07-13): 'messages' = the owner-mailbox Messages panel.
    // Agents can OPEN it; reading/sending stays human-driven (the panel's
    // send is a two-step human confirm — no send verb by design).
    { verb: 'set_tab', params: '{tab: mailboxes|aliases|messages}', description: 'switch between the Mailboxes, Aliases and Messages tabs' },
    { verb: 'refresh', description: 'reload mailboxes + aliases' },
  ],
  'gamemagick.nextcloud': [
    { verb: 'open_folder', params: '{path}', description: 'open a folder by path' },
    { verb: 'up', description: 'go up one folder' },
    { verb: 'refresh', description: 'reload the current folder' },
    { verb: 'download', params: '{name|path}', description: 'download a file from the on-screen list' },
    { verb: 'preview', params: '{name|path}', description: 'preview an image/pdf/audio/video/text file inline' },
    { verb: 'close_preview', description: 'close the open preview' },
    { verb: 'inspect_archive', params: '{name|path}', description: "list a zip/tar/gz file's contents without extracting" },
    // ⛔ No extract verb here on purpose. Extraction WRITES, and this bus is
    // fire-and-forget with no confirmation step — an agent that wants an archive
    // unpacked uses the nextcloud_extract_archive tool, which reports what it did.
  ],
  'gamemagick.google-drive': [
    // Discovery stays human (the Google Picker needs the browser + a human), so
    // every verb operates on the CURRENT on-screen list only — an id|name that
    // is not on screen is a silent no-op.
    { verb: 'open_folder', params: '{id|name}', description: 'open a picked folder from the on-screen list (must be visible)' },
    { verb: 'up', description: 'go up one folder level (back toward the picked set)' },
    { verb: 'refresh', description: 'reload the current folder (no-op at the picked root)' },
    { verb: 'preview', params: '{id|name}', description: 'open a file’s inline preview from the on-screen list' },
    { verb: 'download', params: '{id|name}', description: 'download a file from the on-screen list to disk' },
    { verb: 'open_external', params: '{id|name}', description: 'open a file in Google Drive in a new browser tab' },
    { verb: 'close_preview', description: 'close the inline preview pane' },
    // P4 WRITES — gated by the "Auto-approve agent edits" toggle (default OFF →
    // stage a human Confirm). `trash` ALWAYS asks for Confirm even when the toggle
    // is on (destructive). `create_folder` uses the current on-screen folder as parent.
    { verb: 'create_folder', params: '{name}', description: 'create a folder in the current on-screen folder (asks Confirm unless auto-approve is on)' },
    { verb: 'rename', params: '{id|name, new_name}', description: 'rename an on-screen file/folder (asks Confirm unless auto-approve is on)' },
    { verb: 'trash', params: '{id|name}', description: 'move an on-screen file to Trash — recoverable; ALWAYS asks for Confirm' },
    // P5 — export is read (no Confirm); move is a gated write (asks Confirm unless
    // auto-approve is on). Both operate on the on-screen list only.
    { verb: 'export', params: '{id|name, format: docx|xlsx|pptx}', description: 'download a native Google Doc/Sheet/Slides as an Office file (Word/Excel/PowerPoint)' },
    { verb: 'move', params: '{id|name, to: folder id|name}', description: 'move an on-screen file/folder into another on-screen folder (asks Confirm unless auto-approve is on)' },
    // OUTWARD-FACING — grants another person access; ALWAYS asks for Confirm (even
    // with auto-approve on). On-screen file only; the grantee email is free text.
    { verb: 'share', params: '{id|name, email, role: reader|commenter|writer}', description: 'grant a person access to an on-screen file by email — OUTWARD-FACING; ALWAYS asks for Confirm' },
  ],
  'gamemagick.doc-studio': [
    // P3 — an agent (Pi/voice) authors the on-screen document. Every verb mutates
    // only the IN-EDITOR buffer (set_title/set_theme/add_block/draft) or downloads
    // (export) — NONE persists to the project. PERSISTENCE STAYS HUMAN: saving
    // overwrites the project's doc-studio/document.json in place, so — unlike
    // google-drive's toggle-gated writes — there is deliberately NO `save` verb;
    // the operator clicks Save (⌘S). This makes a misheard "save it" after a
    // `draft` unable to clobber the real document. draft REPLACES the unsaved
    // buffer (recoverable — the human just doesn't Save), like the AI-Draft panel.
    // Block edit/remove/move are deferred (need index addressing).
    // The unknown-type/unknown-theme no-ops are HANDLER-side guards (App.tsx checks
    // ADDABLE_BLOCK_TYPES / the live themes), not a property of blockModel.
    { verb: 'set_title', params: '{text}', description: 'set the document title' },
    { verb: 'set_theme', params: '{theme: an id from the on-screen Theme list, e.g. slate|ember}', description: 'change the document theme; an unknown id is a no-op' },
    { verb: 'add_block', params: '{type: heading|paragraph|image|button|divider|page-break, text?}', description: 'append a block (text applies to heading/paragraph/button); an unknown type is a no-op' },
    { verb: 'draft', params: '{niche, audience, promise?}', description: 'AI-write a Rule-of-One first draft — REPLACES the in-editor document (does NOT save it), marks it unsaved (announce it is writing; ~1-2 min); needs niche + audience' },
    { verb: 'export', params: '{}', description: 'export/download the branded PDF (no persistence)' },
  ],
  // Core-VIEW key (contextKey = `view:designer`), not a module tile — the
  // Designer's PreviewPanel handles it. Lets Pi chat AND voice drive a
  // Blockworld-style voxel app in the preview iframe: the handler posts
  // {type:'bw_cmd', cmd} same-origin into the iframe, whose own command bar is
  // the parser. Benign no-op for previewed apps that don't listen.
  'view:designer': [
    {
      verb: 'blockworld_cmd',
      params: '{cmd — its OUTPUT comes back to you; START with "help"; build:"agent <request>"|"run [ops]"; scenes:"scenes"|"save_scene <n>"|"load_scene <n>"|"fetch_scene <n>"; inspect:"world_info"}',
      description:
        'run ONE command in the previewed Blockworld voxel app and read its output back. ' +
        'This is the ONLY way to build/edit it — never write scene files or edit code. ' +
        'Discover with cmd:"help"; add/load scenes with save_scene/load_scene/fetch_scene; ' +
        'build with cmd:"agent <request>" or cmd:"run [{op,args}]".',
    },
  ],
};

/** Host-level tiling verbs — always available on the Modules view regardless of
 * which tiles are open. Not module-scoped, so they stay statically advertised. */
export const TILING_ACTIONS: ModuleActionSpec[] = [
  { verb: 'open_module', params: '{module_id}', description: 'open a module tile (ids in the available list)' },
  { verb: 'close_module', params: '{module_id?}', description: 'close a tile (focused if omitted)' },
  { verb: 'focus_module', params: '{module_id}', description: 'focus a tile' },
  { verb: 'dock_module', params: '{module_id?, edge: left|right|top|bottom|none}', description: 'dock a tile to a screen edge' },
  { verb: 'maximize_module', params: '{module_id?}', description: 'maximize/restore a tile' },
];

/** The verbs for a given spaContext key (moduleId), or [] if none/unknown. */
export function actionsForKey(key: string): ModuleActionSpec[] {
  return MODULE_ACTIONS[key] ?? [];
}

/** One-line render, e.g. "set_tab {tab: mailboxes|aliases}, refresh". */
export function formatActionsLine(specs: ModuleActionSpec[]): string {
  return specs.map((s) => (s.params ? `${s.verb} ${s.params}` : s.verb)).join(', ');
}
